import { AxiosResponse } from "axios";
import BaseAPI from "../api";

const path = "inheritance-transfer";

/**
 * envelope มาตรฐานของ endpoint ชุดนี้ ต่างจาก API รุ่นเก่าในโปรเจกต์ที่คืน array ตรงๆ
 * ฝั่งหน้าจอจึงต้องอ่าน res.data.data ไม่ใช่ res.data
 */
export type BaseResponse<T> = {
  success: boolean;
  status: number;
  message: string;
  data: T;
};

/** รูปแบบของ nestjs-typeorm-paginate — total อยู่ใน meta ไม่ใช่ header 'total' แบบ API เก่า */
export type Paginated<T> = {
  items: T[];
  meta: {
    totalItems: number;
    itemCount: number;
    itemsPerPage: number;
    totalPages: number;
    currentPage: number;
  };
  links: { first: string; previous: string; next: string; last: string };
};

export type InheritanceStatus = "WAITING" | "APPROVE" | "NOT_APPROVE";

export interface InheritanceTransferParams {
  keyword?: string;
  status?: InheritanceStatus;
  date_from?: string;
  date_to?: string;
  page?: number;
  limit?: number;
}

export interface InheritanceTransferRow {
  id_inheritance_transfer: number;
  inheritance_no: string;
  customerId_owner: number;
  owner_fullname: string;
  customerId_heir: number;
  heir_fullname: string;
  relationshipsId: number | null;
  relationship_name: string | null;
  request_date: string | null;
  reviewed_date: string | null;
  reviewed_id: number | null;
  reviewer_name: string | null;
  status: InheritanceStatus;
  stall_count: number;
}

/** จำนวนคำร้องแยกตามสถานะ ใช้โชว์ตัวเลขข้างชื่อใน dropdown ตัวกรอง */
export interface InheritanceSummary {
  all: number;
  WAITING: number;
  APPROVE: number;
  NOT_APPROVE: number;
}

export interface OwnerSearchParams {
  keyword?: string;
  /** default 1 = เฉพาะผู้ที่ถือสิทธิแผงค้าอยู่จริง */
  only_has_stall?: 0 | 1;
  /** default 0 = ไม่กรองว่ามีผู้รับมรดกแล้วหรือยัง */
  only_has_heir?: 0 | 1;
  page?: number;
  limit?: number;
}

export interface OwnerSuggestion {
  /** key ของผู้โอน — ใช้กับทุก endpoint ถัดไป ไม่ใช่ customerId */
  userId: number;
  /** null = ยังไม่มีข้อมูลลูกค้า ระบุผู้รับมรดกไม่ได้ */
  customerId: number | null;
  owner_fullname: string;
  /** backend mask มาแล้ว ('345xxxxxxx757') ห้าม mask ซ้ำ */
  idCard: string;
  mobilePhone: string;
  heir_count: number;
  stall_count: number;
}

export interface HeirOption {
  /** ค่าที่ต้องส่งกลับตอน POST / */
  id_customer_agent: number;
  customerId_heir: number;
  userId_heir: number | null;
  agent_type: "HEIR";
  agent_type_name: string;
  heir_fullname: string;
  idCard: string;
  mobilePhone: string;
}

export interface OwnerStall {
  leaseAgreementId: number;
  branchId: number | null;
  stallId: number;
  zoneId: number | null;
  document_date: string | null;
  lease_agreement_no: string | null;
  tenant_name: string | null;
  zone_name: string | null;
  stall_name: string | null;
  stall_number: string | null;
  stall_type_name: string | null;
  appointment_date: string | null;
  date_start: string | null;
  date_end: string | null;
  lease_status: "ACTIVE" | "INACTIVE" | "CANCEL" | "TRANSFER";
}

/** snapshot แผงค้า ณ วันยื่นคำร้อง — ค่าอาจต่างจาก OwnerStall ที่เป็นข้อมูลสด ถือเป็นเรื่องปกติ */
export type InheritanceTransferStall = OwnerStall & {
  id_inheritance_transfer_stall: number;
  id_inheritance_transfer: number;
  status: "ACTIVE";
  created_id: number | null;
  created_date: string;
};

export interface InheritanceTransferDetail {
  id_inheritance_transfer: number;
  inheritance_no: string;
  userId_owner: number | null;
  customerId_owner: number;
  owner_fullname: string;
  owner_idCard: string;
  customerId_heir: number;
  heir_fullname: string;
  heir_idCard: string;
  heir_mobilePhone: string;
  id_customer_agent: number | null;
  relationshipsId: number | null;
  relationship_name: string | null;
  request_date: string | null;
  reviewed_date: string | null;
  reviewer_name: string | null;
  review_note: string | null;
  status: InheritanceStatus;
  stalls: InheritanceTransferStall[];
}

export interface InheritanceTransferFile {
  id_inheritance_transfer_file: number;
  id_inheritance_transfer: number;
  id_inheritance_transfer_log: number | null;
  /** '1725690000000-ใบมรณบัตร.pdf' — timestamp นำหน้ากันชื่อชนกัน */
  file_name: string;
  file_path: string;
  mimetype: string | null;
  file_size: number | null;
  status?: "ACTIVE";
  created_id?: number | null;
  created_date?: string;
  /**
   * ห้ามใช้ — static path ที่คืนมาผิด (`public/` เป็น root ของ static server อยู่แล้ว)
   * และไม่ผ่าน auth ใครมี URL ก็เปิดเอกสารได้ ซึ่งขัด PDPA เพราะเป็นสำเนาบัตร/ใบมรณบัตร
   * backend คงไว้เพื่อ backward compatibility เท่านั้น
   */
  file_url?: string;
  /** '/inheritance-transfer/13/file/5/download' — path ไม่รวม global prefix ต่อกับ baseURL ได้เลย */
  download_url: string;
}

export interface InheritanceTransferLog {
  id_inheritance_transfer_log: number;
  id_inheritance_transfer: number;
  action: "CREATE" | "REVIEW" | "DELETE";
  /** null เมื่อ action = CREATE */
  result: "APPROVE" | "NOT_APPROVE" | null;
  note: string | null;
  action_id: number | null;
  /** ชื่อผู้ทำรายการ snapshot ตอนทำ ใช้แสดงได้เลยไม่ต้อง join user */
  action_name: string | null;
  action_date: string | null;
  status: "ACTIVE";
  created_id: number | null;
  created_date: string;
  files: InheritanceTransferFile[];
}

export interface CreateInheritanceBody {
  /** OwnerSuggestion.userId — ไม่ใช่ customerId backend หา customerId เองจาก userId */
  userId_owner: number;
  /** HeirOption.id_customer_agent */
  id_customer_agent: number;
  relationshipsId?: number;
  note?: string;
}

export interface CreateInheritanceResponse {
  id_inheritance_transfer: number;
  inheritance_no: string;
  stall_count: number;
}

/**
 * axios 0.22 ประกาศ AxiosResponse<T = never> ถ้าไม่ใส่ generic res.data จะเป็น never
 * ทุกเมธอดจึงต้องระบุ type ของ data ให้ครบ
 */
export default class InheritanceTransferApi extends BaseAPI {
  static findAll(
    params: InheritanceTransferParams
  ): Promise<AxiosResponse<BaseResponse<Paginated<InheritanceTransferRow>>>> {
    return this.api.get(path, { params: { ...params } });
  }

  static summary(
    params: Omit<InheritanceTransferParams, "status" | "page" | "limit">
  ): Promise<AxiosResponse<BaseResponse<InheritanceSummary>>> {
    return this.api.get(`${path}/summary`, { params: { ...params } });
  }

  static ownerSearch(
    params: OwnerSearchParams
  ): Promise<AxiosResponse<BaseResponse<Paginated<OwnerSuggestion>>>> {
    return this.api.get(`${path}/owner/search`, { params: { ...params } });
  }

  /** userId ของผู้โอน ไม่ใช่ customerId */
  static ownerHeir(userId: number): Promise<AxiosResponse<BaseResponse<HeirOption[]>>> {
    return this.api.get(`${path}/owner/${userId}/heir`);
  }

  /** ข้อมูลแผงค้าสด ณ ตอนเรียก (modal สร้างคำร้อง) — คนละชุดกับ snapshot ใน GET /:id */
  static ownerStall(userId: number): Promise<AxiosResponse<BaseResponse<OwnerStall[]>>> {
    return this.api.get(`${path}/owner/${userId}/stall`);
  }

  static create(
    body: CreateInheritanceBody
  ): Promise<AxiosResponse<BaseResponse<CreateInheritanceResponse>>> {
    return this.api.post(path, body);
  }

  static findById(
    id: number
  ): Promise<AxiosResponse<BaseResponse<InheritanceTransferDetail>>> {
    return this.api.get(`${path}/${id}`);
  }

  static log(id: number): Promise<AxiosResponse<BaseResponse<InheritanceTransferLog[]>>> {
    return this.api.get(`${path}/${id}/log`);
  }

  /**
   * multipart ยิงตรง ไม่ใช่ 2-step (สร้าง record -> upload ด้วย refId) แบบ endpoint อื่นในระบบ
   * ห้าม set Content-Type เอง ต้องให้ browser ใส่ boundary ของ multipart
   */
  static review(id: number, body: FormData): Promise<AxiosResponse<BaseResponse<boolean>>> {
    return this.api.post(`${path}/${id}/review`, body);
  }

  /** ไฟล์แนบทั้งหมดของคำร้อง (รวมทุกครั้งที่พิจารณา) — ถ้าต้องแยกตามครั้ง ให้ใช้ log() ที่ group ไว้แล้ว */
  static fileAll(
    id: number
  ): Promise<AxiosResponse<BaseResponse<InheritanceTransferFile[]>>> {
    return this.api.get(`${path}/${id}/file`);
  }

  /**
   * ดาวน์โหลดไฟล์แนบ — ผ่าน JWT guard จึงต้องยิงด้วย axios instance ไม่ใช่เปิด URL ตรง
   * ส่ง download_url ที่ backend คืนมาได้เลย (axios combineURLs ต่อกับ baseURL ที่มี prefix ให้)
   * responseType blob บังคับ ไม่งั้น axios parse เป็น string แล้วไฟล์ binary เสีย
   */
  static downloadFile(downloadUrl: string): Promise<AxiosResponse<Blob>> {
    return this.api.get(downloadUrl, { responseType: "blob" });
  }
}
